import { registerPlugin } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { mimeTypeFor } from './download-rules';
import { isNative } from './platform';

/**
 * 把一个文件交到用户手里（CR-043；App 端 CR-054 改成存进手机）。
 *
 * - 浏览器：临时 object URL + `<a download>`，浏览器自己存到下载目录；
 * - App：安卓 WebView 里 `<a download>` 是失效的（ExportButton 时代就踩过）。
 *   CR-043 做的是「写进缓存目录再弹系统分享」，客户原话「用app的话，下载功能是分享。
 *   能变成下载到本机不？客户表示有时候需要手机下载后打印出来」——
 *   现在**直接存进手机的「下载」文件夹**（本地插件 `DownloadsPlugin.java`，理由写在它头上），
 *   页面再给「打开」「分享」两个后续动作（`SavedFileNotice`）。
 *
 * 写文件只收 base64，整包要先读进内存：所以 App 端下载前会告诉服务端上限（`limitBytes`），
 * 超了服务端直接拒绝，不让人下到一半失败。
 */
export const NATIVE_DOWNLOAD_LIMIT_BYTES = 200 * 1024 * 1024;

/** App 里存好之后交给页面的：实际存成的名字（同名会加序号），以及两个后续动作 */
export interface SavedFile {
  name: string;
  open: () => Promise<void>;
  share: () => Promise<void>;
}

interface DownloadsPlugin {
  save(options: { source: string; name: string; mimeType: string }): Promise<{ uri: string; name: string }>;
  open(options: { uri: string; mimeType: string }): Promise<void>;
  /** 装一个下载好的 APK（CR-067）。没拿到「安装未知应用」授权时它会打开设置页并返回 settings */
  installApk(options: { source: string }): Promise<{ opened: 'installer' | 'settings' }>;
}

const Downloads = registerPlugin<DownloadsPlugin>('Downloads');

/** 缓存里的中转目录。每次先清空：200MB 的包不能在手机上越攒越多 */
const CACHE_DIR = 'downloads';

/** 更新包单独一个缓存目录，不和下载的中转目录混：下一次下载会把中转目录整个清掉 */
const UPDATE_DIR = 'updates';

/** 浏览器里交给浏览器另存为，返回 null；App 里存进「下载」，返回存好的文件 */
export async function saveBlob(blob: Blob, filename: string): Promise<SavedFile | null> {
  if (!isNative) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // 立刻释放会让 Chrome 来不及开始下载，延后一点
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    return null;
  }

  const mimeType = mimeTypeFor(filename, blob.type);
  await Filesystem.rmdir({ path: CACHE_DIR, directory: Directory.Cache, recursive: true }).catch(() => undefined);
  const { uri: cached } = await Filesystem.writeFile({
    path: `${CACHE_DIR}/${filename}`,
    data: await toBase64(blob),
    directory: Directory.Cache,
    recursive: true,
  });
  const saved = await Downloads.save({ source: cached, name: filename, mimeType });

  return {
    name: saved.name,
    open: () => Downloads.open({ uri: saved.uri, mimeType }),
    // 分享用缓存里那份：Share 插件只收 file://，「下载」里那份是 MediaStore 的 content://
    share: async () => {
      await Share.share({ title: saved.name, url: cached, dialogTitle: saved.name });
    },
  };
}

/**
 * 把更新包写进 App 自己的缓存目录，返回它的 `file://` 地址（网页端返回 null）。
 *
 * **不走「下载」文件夹**（CR-067）：
 * - 工人没要这个文件。每更新一次就在他手机的「下载」里留一个 6MB 的 APK，一年下来是一堆垃圾；
 * - 「下载」那份是 MediaStore 的 `content://`，**我们授不了读权限给系统安装器**
 *   （`FLAG_GRANT_READ_URI_PERMISSION` 只对自己的 provider 有效），装的时候可能直接失败。
 *   缓存目录这份由自家 FileProvider 发出去，授权是确定的。
 *
 * 每次写之前清空这个目录：装完的包没人再要，系统回收缓存之前也别让它占着地方。
 */
export async function cacheUpdate(blob: Blob, filename: string): Promise<string | null> {
  if (!isNative) return null;
  await Filesystem.rmdir({ path: UPDATE_DIR, directory: Directory.Cache, recursive: true }).catch(() => undefined);
  const { uri } = await Filesystem.writeFile({
    path: `${UPDATE_DIR}/${filename}`,
    data: await toBase64(blob),
    directory: Directory.Cache,
    recursive: true,
  });
  return uri;
}

/**
 * 装一个下载好的 APK（CR-067，App 内更新）。`source` 是 `cacheUpdate` 给的 `file://` 地址。
 *
 * 返回 `settings` 表示这台手机还没给「安装未知应用」的授权、设置页已经打开，
 * 让工人开完再点一次；`installer` 表示系统安装界面已经弹出来了 ——
 * **最后那下「安装」只能工人自己点**，安卓不允许非商店应用静默替换自己。
 */
export async function installApk(source: string): Promise<'installer' | 'settings'> {
  const { opened } = await Downloads.installApk({ source });
  return opened;
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}
